import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dah2qkbp14a1h3mr6b90';
const MAX_LENGTH = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

const SUBJECTS = [
  '投稿线索',
  '内容更正',
  '授权转载',
  '商务合作',
  '读者反馈',
];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const description = String(formData.get('description') || '');
    if (description.length > MAX_LENGTH) {
      setStatus('error');
      setMessage(`内容说明不能超过 ${MAX_LENGTH} 个字符，请精简后再提交。`);
      return;
    }

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('提交成功，编辑组会在 48 小时内与你联系。');
      form.reset();
      setLength(0);
      return;
    }

    setStatus('submitting');
    setMessage('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const failed =
        !response.ok ||
        (parsed?.code ? parsed.code !== 'OK' : false) ||
        /spam/i.test(String(serverMsg));

      if (failed) {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setMessage('提交成功，编辑组会在 48 小时内与你联系。');
      form.reset();
      setLength(0);
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/40';

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      aria-label="联系与投稿表单"
      className="space-y-5"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-2 block text-sm text-foreground-300">
            称呼 <span className="text-accent-400">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-2 block text-sm text-foreground-300">
            联系邮箱 <span className="text-accent-400">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="方便我们回复你"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="mb-2 block text-sm text-foreground-300">
          事项类型 <span className="text-accent-400">*</span>
        </label>
        <select id="contact-subject" name="subject" required defaultValue="投稿线索" className={inputClass}>
          {SUBJECTS.map((item) => (
            <option key={item} value={item} className="bg-background-100 text-foreground-100">
              {item}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor="contact-description" className="block text-sm text-foreground-300">
            内容说明 <span className="text-accent-400">*</span>
          </label>
          <span className={`text-xs ${length > MAX_LENGTH ? 'text-accent-400' : 'text-foreground-600'}`}>
            {length}/{MAX_LENGTH}
          </span>
        </div>
        <textarea
          id="contact-description"
          name="description"
          required
          maxLength={MAX_LENGTH}
          rows={6}
          onChange={(event) => setLength(event.target.value.length)}
          placeholder="请尽量写清时间、地点、涉及方以及你掌握的材料类型。涉及隐私的部分可以先做打码说明。"
          className={`${inputClass} resize-y leading-relaxed`}
        ></textarea>
        <p className="mt-2 text-xs text-foreground-600">
          最多 {MAX_LENGTH} 字。投稿默认匿名处理，我们不会对外披露线索人身份。
        </p>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-ghost-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '提交中...' : '提交信息'}
          <i className="ri-send-plane-fill"></i>
        </button>
        <p className="text-xs text-foreground-600">提交后编辑组通常会在 48 小时内回复。</p>
      </div>

      {message ? (
        <p
          role="status"
          className={`rounded-md border px-4 py-3 text-sm ${
            status === 'error'
              ? 'border-accent-500/40 bg-accent-500/10 text-accent-300'
              : 'border-secondary-500/40 bg-secondary-500/10 text-secondary-300'
          }`}
        >
          {message}
        </p>
      ) : null}
    </form>
  );
}